import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import { progressService } from "../../../../../../services/progressService";
import { UserContext } from "../../../../../../UserContext";
import StakeholderProfileForm from "./StakeholderProfileForm";
import CompletionScreen from "./CompletionScreen";

const ProfileStep2Adapter = ({
  userId,
  subsectionId,
  onNextStep,
  onPreviousStep,
  onPhaseChange,
  initialSelectedStakeholders,
}) => {
  const [internalPhase, setInternalPhase] = useState("form");
  const [allStakeholdersData, setAllStakeholdersData] = useState({});
  const [selectedStakeholders, setSelectedStakeholders] = useState(
    initialSelectedStakeholders || [null, null]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user } = useContext(UserContext);
  const userIdContext = user?.user_id;
  const effectiveUserId = userId || userIdContext;

  const debounceTimer = useRef(null);

  console.log("🎯 ProfileStep2Adapter Props:");
  console.log("  ├─ initialSelectedStakeholders:", initialSelectedStakeholders);
  console.log("  └─ Stakeholder 2:", initialSelectedStakeholders?.[1]);

  useEffect(() => {
    if (onPhaseChange) onPhaseChange("immersive");
  }, [onPhaseChange]);

  // ✅ Update selectedStakeholders when initialSelectedStakeholders changes
  useEffect(() => {
    if (initialSelectedStakeholders) {
      console.log("✅ Using stakeholders from selection screen:", initialSelectedStakeholders);
      setSelectedStakeholders(initialSelectedStakeholders);
    }
  }, [initialSelectedStakeholders]);

  // ✅ Load saved data - ONLY allStakeholdersData (by ID)
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (!effectiveUserId || !subsectionId) {
          setAllStakeholdersData({});
          setIsLoading(false);
          return;
        }

        let res = null;
        try {
          res = await progressService.getUserProgress(
            effectiveUserId,
            subsectionId
          );
        } catch (err) {
          console.error(
            "[ProfileStep2Adapter] getUserProgress error (maybe 404):",
            err
          );
        }

        if (!res) {
          console.log(
            "[ProfileStep2Adapter] No existing progress, starting fresh"
          );
          setAllStakeholdersData({});
          setIsLoading(false);
          return;
        }

        const data = res;
        const payload = data?.data?.payload || data?.payload || null;

        console.log("[ProfileStep2Adapter] 📦 Loaded payload:", payload);

        if (!payload) {
          setAllStakeholdersData({});
          setIsLoading(false);
          return;
        }

        if (payload.allStakeholdersData) {
          console.log("✅ Loading allStakeholdersData:", payload.allStakeholdersData);
          setAllStakeholdersData(payload.allStakeholdersData);
        } else {
          setAllStakeholdersData({});
        }
      } catch (err) {
        console.error("[ProfileStep2Adapter] Failed to load data:", err);
        setError("Failed to load data");
        setAllStakeholdersData({});
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [effectiveUserId, subsectionId]);

  // ✅ FIX: Save in BOTH formats (by ID + by position)
  const saveProgress = useCallback(
    async (data, status = "in_progress") => {
      if (!effectiveUserId || !subsectionId) return;
      setIsSaving(true);

      try {
        const stakeholder1 = selectedStakeholders[0];
        const stakeholder2 = selectedStakeholders[1];

        // Build stakeholderData in position format
        const stakeholderData = {};
        
        if (stakeholder1?.id) {
          const s1Data = data.allStakeholdersData?.[stakeholder1.id] || allStakeholdersData[stakeholder1.id] || {};
          stakeholderData.stakeholder1 = {
            name: stakeholder1.name,
            role: stakeholder1.role,
            ...s1Data,
          };
        }

        if (stakeholder2?.id) {
          const s2Data = data.allStakeholdersData?.[stakeholder2.id] || allStakeholdersData[stakeholder2.id] || {};
          stakeholderData.stakeholder2 = {
            name: stakeholder2.name,
            role: stakeholder2.role,
            ...s2Data,
          };
        }

        const dataToSave = {
          type: "widget",
          payload: {
            selectedStakeholders: selectedStakeholders,
            allStakeholdersData: data.allStakeholdersData ?? allStakeholdersData,
            stakeholderData: stakeholderData, // ✅ Added position-based format
          },
        };

        console.log(
          "[ProfileStep2Adapter] 💾 Saving progress with dual format:",
          dataToSave
        );

        await progressService.saveProgress(
          effectiveUserId,
          subsectionId,
          dataToSave,
          status
        );
      } catch (err) {
        console.error("[ProfileStep2Adapter] Failed to save progress:", err);
        setError("Failed to save progress");
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [
      effectiveUserId,
      subsectionId,
      selectedStakeholders,
      allStakeholdersData,
    ]
  );

  const handleDataChange = useCallback(
    (data) => {
      if (!selectedStakeholders[1]?.id) return;

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      
      debounceTimer.current = setTimeout(() => {
        const stakeholder = selectedStakeholders[1];
        console.log("[ProfileStep2Adapter] 📝 Data changed for ID:", stakeholder.id, data);

        // ✅ Save by stakeholder ID, not position
        setAllStakeholdersData((prev) => ({
          ...prev,
          [stakeholder.id]: {
            name: stakeholder.name,
            role: stakeholder.role,
            ...(prev[stakeholder.id] || {}),
            ...data,
          },
        }));

        console.log("[ProfileStep2Adapter] 🔄 Data updated for:", stakeholder.name);
      }, 300);
    },
    [selectedStakeholders]
  );

  // ✅ Auto-save effect
  useEffect(() => {
    if (Object.keys(allStakeholdersData).length === 0) return;
    
    const saveTimer = setTimeout(() => {
      if (selectedStakeholders[1]?.id) {
        console.log("[ProfileStep2Adapter] 💾 Auto-saving progress");
        saveProgress({
          allStakeholdersData,
        });
      }
    }, 1000);

    return () => clearTimeout(saveTimer);
  }, [allStakeholdersData, saveProgress, selectedStakeholders]);

  const handleFormComplete = async (formData) => {
    if (!selectedStakeholders[1]?.id) {
      alert("No stakeholder2 selected");
      return;
    }

    const cleanedFormData = {
      values: (formData.values || []).filter((v) => v.trim() !== ""),
      kpis: (formData.kpis || []).filter((k) => k.trim() !== ""),
      interests: (formData.interests || []).filter((i) => i.trim() !== ""),
    };

    const stakeholder = selectedStakeholders[1];

    // ✅ Save by stakeholder ID
    const updatedAllData = {
      ...allStakeholdersData,
      [stakeholder.id]: {
        name: stakeholder.name,
        role: stakeholder.role,
        ...cleanedFormData,
      },
    };

    setAllStakeholdersData(updatedAllData);

    try {
      console.log("[ProfileStep2Adapter] ✅ Form completed, saving:", {
        stakeholderId: stakeholder.id,
        stakeholderName: stakeholder.name,
        data: updatedAllData[stakeholder.id],
      });

      await saveProgress(
        {
          allStakeholdersData: updatedAllData,
        },
        "in_progress"
      );
    } catch (err) {
      alert("Error saving data");
      return;
    }

    setInternalPhase("preview");
    setTimeout(() => setInternalPhase("completion"), 5000);
  };

  const handleFinalComplete = () => {
    if (typeof onNextStep === "function") onNextStep();
  };

  const handleEditProfile = () => setInternalPhase("form");

  const handleBackToSelection = () => {
    if (typeof onPreviousStep === "function") onPreviousStep();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-red-50 text-red-600">
        <p>{error}</p>
      </div>
    );
  }

  const stakeholder = selectedStakeholders[1];
  if (!stakeholder) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">No stakeholder selected</p>
          <button
            onClick={handleBackToSelection}
            className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Go Back to Selection
          </button>
        </div>
      </div>
    );
  }

  // ✅ CRITICAL: Load data based on stakeholder ID
  const initialData = allStakeholdersData[stakeholder.id] || {
    values: ["", "", ""],
    kpis: ["", "", ""],
    interests: ["", "", ""],
  };

  console.log("[ProfileStep2Adapter] 👤 Current stakeholder:", stakeholder);
  console.log("[ProfileStep2Adapter] 🔑 Stakeholder ID:", stakeholder.id);
  console.log(
    "[ProfileStep2Adapter] 📦 Data for this ID:",
    allStakeholdersData[stakeholder.id]
  );
  console.log(
    "[ProfileStep2Adapter] 📤 initialData passed to form:",
    initialData
  );

  return (
    <>
      {internalPhase === "form" && (
        <StakeholderProfileForm
          key={`form-${stakeholder.id}`}
          stakeholder={stakeholder}
          stakeholderNumber={2}
          initialData={initialData}
          onComplete={handleFormComplete}
          onDataChange={handleDataChange}
          isSaving={isSaving}
          loading={false}
        />
      )}

      {internalPhase === "preview" && (
        <CompletionScreen
          stakeholderData={initialData}
          stakeholder={stakeholder}
          stakeholderNumber={2}
          showEditProfile={false}
          showContinue={false}
          userName={user?.firstName || ""}
        />
      )}

      {internalPhase === "completion" && (
        <CompletionScreen
          stakeholderData={initialData}
          stakeholder={stakeholder}
          stakeholderNumber={2}
        showEditProfile={true}
        showContinue={true}
        onNext={handleFinalComplete}
        onEditProfile={handleEditProfile}
        onBackToSelection={handleBackToSelection}
        userName={user?.firstName || ""}
        continueButtonText="Continue"
      />
      )}
    </>
  );
};

export default ProfileStep2Adapter;
